package org.example.project.engineletter

import kotlin.coroutines.resume
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.events.EventListener
import org.w3c.files.Blob
import org.w3c.files.File

suspend fun renderLetterDocumentPdfFile(root: HTMLElement, fileName: String): File {
    awaitNextAnimationFrame()
    awaitNextAnimationFrame()
    awaitImageLoading(root)

    val exportHost = createExportHost()
    val pageNodes = root.querySelectorAll("[data-letter-pdf-page='true']")
    for (index in 0 until pageNodes.length) {
        val page = pageNodes.item(index) as? HTMLElement ?: continue
        exportHost.appendChild(page.cloneNode(deep = true))
    }
    awaitImageLoading(exportHost)

    try {
        val pages = exportHost.querySelectorAll("[data-letter-pdf-page='true']")
        val pdfBlob = suspendCancellableCoroutine<Blob> { continuation ->
            val jsPdfCtor = resolveJsPdfConstructor()
            val pdf = js("new jsPdfCtor({ orientation: 'p', unit: 'pt', format: 'a4', compress: true })")

            val scope = kotlinx.coroutines.MainScope()
            scope.launch {
                try {
                    for (index in 0 until pages.length) {
                        val page = pages.item(index) as? HTMLElement ?: continue
                        val canvas = renderPageCanvas(page)
                        val imageData = canvas.toDataURL("image/png")
                        if (index > 0) pdf.addPage("a4", "p")
                        pdf.addImage(imageData, "PNG", 0, 0, 595.28, 841.89, "", "FAST")
                    }
                    continuation.resume(pdf.output("blob").unsafeCast<Blob>())
                } catch (t: Throwable) {
                    continuation.cancel(t)
                } finally {
                    scope.cancel()
                }
            }
        }

        return js("new File([pdfBlob], fileName, { type: 'application/pdf' })").unsafeCast<File>()
    } finally {
        exportHost.remove()
    }
}

private fun resolveJsPdfConstructor(): dynamic {
    val module = js("typeof require !== 'undefined' ? require('jspdf') : null")
    val ctor = module?.jsPDF ?: module?.default ?: js("window.jspdf && window.jspdf.jsPDF")
    if (ctor == null) {
        error("jsPDF constructor not available")
    }
    return ctor
}

private suspend fun renderPageCanvas(page: HTMLElement): HTMLCanvasElement {
    val html2canvas = resolveHtml2Canvas()
    val options = js("{}")
    options.backgroundColor = "#ffffff"
    options.useCORS = true
    options.scale = window.devicePixelRatio.coerceAtLeast(2.0)
    options.width = page.scrollWidth
    options.height = page.scrollHeight
    options.windowWidth = page.scrollWidth
    options.windowHeight = page.scrollHeight
    options.scrollX = 0
    options.scrollY = 0
    options.logging = false

    return suspendCancellableCoroutine { continuation ->
        html2canvas(page, options).then({ canvas: dynamic ->
            continuation.resume(canvas.unsafeCast<HTMLCanvasElement>())
        }, { error: dynamic ->
            continuation.cancel(Throwable(error?.toString() ?: "html2canvas failed"))
        })
    }
}

private fun resolveHtml2Canvas(): dynamic {
    val module = js("typeof require !== 'undefined' ? require('html2canvas') : null")
    val fn = module?.default ?: module ?: js("window.html2canvas")
    if (fn == null) {
        error("html2canvas not available")
    }
    return fn
}

private suspend fun awaitNextAnimationFrame() {
    suspendCancellableCoroutine<Unit> { continuation ->
        window.requestAnimationFrame {
            continuation.resume(Unit)
        }
    }
}

private suspend fun awaitImageLoading(root: HTMLElement) {
    val images = root.querySelectorAll("img")
    for (index in 0 until images.length) {
        val image = images.item(index) as? HTMLImageElement ?: continue
        if (image.complete) continue
        suspendCancellableCoroutine<Unit> { continuation ->
            lateinit var listener: EventListener
            listener = EventListener {
                image.removeEventListener("load", listener)
                image.removeEventListener("error", listener)
                continuation.resume(Unit)
            }
            image.addEventListener("load", listener)
            image.addEventListener("error", listener)
        }
    }
}

private fun createExportHost(): HTMLElement {
    val host = document.createElement("div") as HTMLElement
    host.style.position = "fixed"
    host.style.left = "-10000px"
    host.style.top = "0"
    host.style.width = "794px"
    host.style.zIndex = "-1"
    host.style.background = "#ffffff"
    document.body?.appendChild(host)
    return host
}
